import {
  Store,
  BookingSlot,
  MediaType,
  StoreTier,
  Province,
  SlotStatus,
  StorePosition,
  PositionType,
} from './types'

const brandNames = [
  'Chanel',
  'Lancôme',
  'Estée Lauder',
  'Clinique',
  'Clarins',
  'Tom Ford',
  "L'Oréal",
  'Maybelline',
  'Revlon',
  'NYX',
  'Elizabeth Arden',
  'Dermalogica',
  'Bioderma',
  'Skoon',
  'The Ordinary',
  'Benefit',
  'NARS',
  'Shiseido',
  'Hugo Boss',
  'Gucci',
]

const priceByMediaType: Record<MediaType, number> = {
  'Digital Outposts': 3250,
  'Digital Drive Aisle': 3750,
  'Digital Windows': 3500,
}

function priceForPositionType(type: PositionType): number {
  return type === 'outpost' ? 3250 : 3750
}

function mediaTypeForPositionType(type: PositionType): MediaType {
  return type === 'outpost' ? 'Digital Outposts' : 'Digital Drive Aisle'
}

// ═══════════════════════════════════════
// PER-STORE POSITIONS
// ═══════════════════════════════════════

const storePositionMap: Record<string, Omit<StorePosition, 'id'>[]> = {
  'V&A Waterfront': [
    { name: 'Screen 1 – Menswear 5', type: 'outpost' },
    { name: 'Screen 2 – Womenswear', type: 'outpost' },
    { name: 'Screen 3 – Sunglasses Hut', type: 'outpost' },
    { name: 'Screen 4 – Beauty Drive Aisle', type: 'outpost' },
    { name: 'Drive Aisle – Womenswear', type: 'cylinder' },
  ],
  'Sandton City': [
    { name: 'Screen 1 – Beauty Drive Aisle 2 Fragrances', type: 'outpost' },
    { name: 'Screen 2 – Beauty Drive Aisle 1 (Country Road)', type: 'outpost' },
    { name: 'Screen 3 – Womenswear/Trenery', type: 'outpost' },
    { name: 'Screen 4 – Menswear', type: 'outpost' },
    { name: 'Drive Aisle – Menswear', type: 'cylinder' },
  ],
  Gateway: [
    { name: 'Screen 1 – Main Drive Aisle', type: 'outpost' },
    { name: "Screen 2 – Women's Wear", type: 'outpost' },
    { name: 'Screen 3 – Lingerie', type: 'outpost' },
  ],
  'Mall of Africa': [
    { name: 'Screen 1 – Beauty Drive Aisle 2 (Menswear)', type: 'outpost' },
    { name: "Screen 2 – Women's Wear 3", type: 'outpost' },
    { name: 'Screen 3 – Main Drive Aisle P1', type: 'outpost' },
    { name: 'Screen 4 – Main Drive Aisle P2', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle P1', type: 'cylinder' },
  ],
  Eastgate: [
    { name: 'Screen 1 – Beauty Drive Aisle 2', type: 'outpost' },
    { name: 'Screen 2 – Till Entrance', type: 'outpost' },
    { name: 'Screen 3 – Lingerie / Forever New', type: 'outpost' },
    { name: 'Screen 4 – Menswear', type: 'outpost' },
    { name: 'Drive Aisle – Beauty Drive Aisle 2', type: 'cylinder' },
  ],
  Cavendish: [
    { name: 'Screen 1 – Tills', type: 'outpost' },
    { name: 'Screen 2 – Beauty Drive Aisle 2', type: 'outpost' },
    { name: 'Drive Aisle – Beauty Drive Aisle 2', type: 'cylinder' },
  ],
  'Canal Walk': [
    { name: 'Screen 1 – Main Drive Aisle', type: 'outpost' },
    { name: 'Screen 2 – Main Drive Menswear', type: 'outpost' },
    { name: 'Screen 3 – Womenswear', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle', type: 'cylinder' },
  ],
  Westville: [
    { name: 'Screen 1 – Beauty', type: 'outpost' },
    { name: 'Screen 2 – Main Drive Aisle', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle', type: 'cylinder' },
  ],
  'Somerset Mall': [
    { name: 'Screen 1 – Main Drive Aisle (Beauty)', type: 'outpost' },
    { name: "Screen 2 – Women's Aisle", type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle', type: 'cylinder' },
  ],
  Walmer: [
    { name: 'Screen 1 – Main Drive Aisle', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle', type: 'cylinder' },
  ],
  Menlyn: [
    { name: "Screen 1 – Main Drive Aisle P4 (Men's)", type: 'outpost' },
    { name: 'Screen 2 – Beauty Drive Aisle P2', type: 'outpost' },
    { name: 'Screen 3 – Main Drive Aisle P3 (Food Market)', type: 'outpost' },
    { name: 'Screen 4 – Main Drive Aisle P1 (Country Road)', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle', type: 'cylinder' },
  ],
  Midlands: [
    { name: 'Screen 1 – Main Drive Aisle (Womenswear)', type: 'outpost' },
    { name: 'Screen 2 – Main Drive Aisle (Sunglass Hut)', type: 'outpost' },
  ],
  Fourways: [
    { name: 'Screen 1 – Main Drive Aisle', type: 'outpost' },
    { name: 'Screen 2 – Beauty', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle', type: 'cylinder' },
  ],
  Brooklyn: [
    { name: 'Screen 1 – Main Drive Aisle P1', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle', type: 'cylinder' },
  ],
  Clearwater: [
    { name: 'Screen 1 – Main Drive Aisle P1', type: 'outpost' },
    { name: 'Screen 2 – Beauty', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle P1', type: 'cylinder' },
  ],
  Centurion: [{ name: 'Screen 1 – Main Drive Aisle P1', type: 'outpost' }],
  Rosebank: [{ name: 'Screen 1 – Main Drive Aisle P1', type: 'outpost' }],
  Cresta: [{ name: 'Screen 1 – Main Drive Aisle P1', type: 'outpost' }],
  'Loch Logan': [
    { name: 'Screen 1 – Main Drive Aisle P1', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle P1', type: 'cylinder' },
  ],
  'East Rand Mall': [
    { name: 'Screen 1 – Main Drive Aisle P1', type: 'outpost' },
    { name: 'Drive Aisle – Main Drive Aisle P1', type: 'cylinder' },
  ],
  Tygervalley: [
    { name: 'Drive Aisle – Main Drive Aisle P1', type: 'cylinder' },
  ],
}

// Default positions for dynamically added stores
export function makeStandardPositions(storeId: string): StorePosition[] {
  return [
    { id: `${storeId}-screen-1`, name: 'Screen 1', type: 'outpost' },
    { id: `${storeId}-drive-aisle`, name: 'Drive Aisle', type: 'cylinder' },
  ]
}

// Seeded PRNG for deterministic slot generation
function createSeededRandom(seed: number) {
  let s = seed
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff
    return (s >>> 0) / 0xffffffff
  }
}

function generateWeekDates(weekNumber: number): { start: string; end: string } {
  const baseDate = new Date('2026-03-03')
  const startDate = new Date(baseDate)
  startDate.setDate(baseDate.getDate() + (weekNumber - 1) * 7)
  const endDate = new Date(startDate)
  endDate.setDate(startDate.getDate() + 6)

  return {
    start: startDate.toISOString().split('T')[0],
    end: endDate.toISOString().split('T')[0],
  }
}

function generateSlotsForPositions(
  storeId: string,
  positions: StorePosition[],
  storeIndex: number,
): BookingSlot[] {
  const slots: BookingSlot[] = []
  const random = createSeededRandom(storeIndex * 1000 + 42)

  positions.forEach((position) => {
    for (let week = 1; week <= 12; week++) {
      const dates = generateWeekDates(week)
      const r = random()

      let status: SlotStatus = 'available'
      let brandName: string | undefined

      if (r < 0.35) {
        status = 'booked'
        brandName = brandNames[Math.floor(random() * brandNames.length)]
      } else if (r < 0.45) {
        status = 'blocked'
      }

      slots.push({
        id: `${storeId}-pos-${position.id}-week-${week}`,
        storeId,
        mediaType: mediaTypeForPositionType(position.type),
        positionId: position.id,
        positionName: position.name,
        weekNumber: week,
        weekStartDate: dates.start,
        weekEndDate: dates.end,
        status,
        brandName,
        price: priceForPositionType(position.type),
      })
    }
  })

  return slots
}

export function generateNewStoreSlots(
  storeId: string,
  tier: StoreTier,
): BookingSlot[] {
  const positions = makeStandardPositions(storeId)
  return generateSlotsForPositions(storeId, positions, Date.now() % 10000)
}

interface StoreDefinition {
  name: string
  location: string
  province: Province
  tier: StoreTier
}

const storeDefinitions: StoreDefinition[] = [
  // Flagship (5)
  {
    name: 'V&A Waterfront',
    location: 'V&A Waterfront, Cape Town',
    province: 'Western Cape',
    tier: 'flagship',
  },
  {
    name: 'Sandton City',
    location: 'Sandton',
    province: 'Gauteng',
    tier: 'flagship',
  },
  {
    name: 'Canal Walk',
    location: 'Century City',
    province: 'Western Cape',
    tier: 'flagship',
  },
  {
    name: 'Mall of Africa',
    location: 'Midrand',
    province: 'Gauteng',
    tier: 'flagship',
  },
  {
    name: 'Gateway',
    location: 'Umhlanga',
    province: 'KwaZulu-Natal',
    tier: 'flagship',
  },

  // High Traffic (7)
  {
    name: 'Eastgate',
    location: 'Bedfordview',
    province: 'Gauteng',
    tier: 'high-traffic',
  },
  {
    name: 'Menlyn',
    location: 'Menlyn, Pretoria',
    province: 'Gauteng',
    tier: 'high-traffic',
  },
  {
    name: 'Cavendish',
    location: 'Claremont',
    province: 'Western Cape',
    tier: 'high-traffic',
  },
  {
    name: 'Fourways',
    location: 'Fourways',
    province: 'Gauteng',
    tier: 'high-traffic',
  },
  {
    name: 'Clearwater',
    location: 'Roodepoort',
    province: 'Gauteng',
    tier: 'high-traffic',
  },
  {
    name: 'Centurion',
    location: 'Centurion',
    province: 'Gauteng',
    tier: 'high-traffic',
  },
  {
    name: 'Tygervalley',
    location: 'Bellville',
    province: 'Western Cape',
    tier: 'high-traffic',
  },

  // Standard (9)
  {
    name: 'Brooklyn',
    location: 'Pretoria',
    province: 'Gauteng',
    tier: 'standard',
  },
  {
    name: 'Cresta',
    location: 'Randburg',
    province: 'Gauteng',
    tier: 'standard',
  },
  {
    name: 'East Rand Mall',
    location: 'Boksburg',
    province: 'Gauteng',
    tier: 'standard',
  },
  {
    name: 'Rosebank',
    location: 'Rosebank',
    province: 'Gauteng',
    tier: 'standard',
  },
  {
    name: 'Loch Logan',
    location: 'Bloemfontein',
    province: 'Free State',
    tier: 'standard',
  },
  {
    name: 'Midlands',
    location: 'Pietermaritzburg',
    province: 'KwaZulu-Natal',
    tier: 'standard',
  },
  {
    name: 'Somerset Mall',
    location: 'Somerset West',
    province: 'Western Cape',
    tier: 'standard',
  },
  {
    name: 'Walmer',
    location: 'Port Elizabeth',
    province: 'Eastern Cape',
    tier: 'standard',
  },
  {
    name: 'Westville',
    location: 'Westville, Durban',
    province: 'KwaZulu-Natal',
    tier: 'standard',
  },
]

export const stores: Store[] = storeDefinitions.map((def, index) => {
  const storeId = `store-${index + 1}`
  const positionDefs = storePositionMap[def.name] || [
    { name: 'Screen 1', type: 'outpost' },
    { name: 'Drive Aisle', type: 'cylinder' },
  ]

  const positions: StorePosition[] = positionDefs.map((p, i) => {
    const isScreen = p.name.startsWith('Screen')
    const screenMatch = p.name.match(/Screen (\d+)/)
    const id =
      isScreen && screenMatch
        ? `${storeId}-screen-${screenMatch[1]}`
        : `${storeId}-drive-aisle${i > 0 && !isScreen ? `-${i}` : ''}`

    return {
      id,
      name: p.name,
      type: p.type,
    }
  })

  return {
    id: storeId,
    name: def.name,
    location: def.location,
    province: def.province,
    tier: def.tier,
    positions,
    slots: generateSlotsForPositions(storeId, positions, index),
  }
})

export function getStoreById(id: string): Store | undefined {
  return stores.find((store) => store.id === id)
}

export function getSlotsByStore(storeId: string): BookingSlot[] {
  const store = getStoreById(storeId)
  return store?.slots || []
}

export function getSlotsByMediaType(
  storeId: string,
  mediaType: MediaType,
): BookingSlot[] {
  return getSlotsByStore(storeId).filter((slot) => slot.mediaType === mediaType)
}

export function countBookedThisWeek(): number {
  const currentWeek = 1
  return stores.reduce((count, store) => {
    const bookedSlots = store.slots.filter(
      (slot) => slot.weekNumber === currentWeek && slot.status === 'booked',
    )
    return count + bookedSlots.length
  }, 0)
}

export function getStoreAvailabilitySummary(
  store: Store,
): Record<MediaType, SlotStatus> {
  const mediaTypes: MediaType[] = [
    'Digital Outposts',
    'Digital Drive Aisle',
    'Digital Windows',
  ]
  const summary: Record<MediaType, SlotStatus> = {
    'Digital Outposts': 'available',
    'Digital Drive Aisle': 'available',
    'Digital Windows': 'available',
  }

  mediaTypes.forEach((mediaType) => {
    const slots = store.slots.filter((s) => s.mediaType === mediaType)
    if (slots.length === 0) {
      summary[mediaType] = 'removed'
      return
    }
    const hasAvailable = slots.some((s) => s.status === 'available')
    const allBooked = slots.every((s) => s.status === 'booked')
    const allBlocked = slots.every((s) => s.status === 'blocked')
    const allInternal = slots.every((s) => s.status === 'internal')
    const allRemoved = slots.every((s) => s.status === 'removed')

    if (allRemoved) {
      summary[mediaType] = 'removed'
    } else if (allInternal) {
      summary[mediaType] = 'internal'
    } else if (allBlocked) {
      summary[mediaType] = 'blocked'
    } else if (allBooked) {
      summary[mediaType] = 'booked'
    } else if (hasAvailable) {
      summary[mediaType] = 'available'
    } else {
      summary[mediaType] = 'booked'
    }
  })

  return summary
}

export function updateStoreSlots(
  storeIds: string[],
  mediaTypes: MediaType[],
  weekNumbers: number[],
  newStatus: SlotStatus,
  brandName?: string,
): number {
  let count = 0
  stores.forEach((store) => {
    if (storeIds.includes(store.id)) {
      store.slots.forEach((slot) => {
        if (
          mediaTypes.includes(slot.mediaType) &&
          weekNumbers.includes(slot.weekNumber) &&
          slot.status === 'available'
        ) {
          slot.status = newStatus
          if (brandName) slot.brandName = brandName
          count++
        }
      })
    }
  })
  return count
}

export const provinces: Province[] = [
  'Western Cape',
  'Gauteng',
  'KwaZulu-Natal',
  'Eastern Cape',
  'Free State',
  'Mpumalanga',
  'Limpopo',
  'North West',
  'Northern Cape',
]

export const tiers: StoreTier[] = ['flagship', 'high-traffic', 'standard']
